import { useCallback, useEffect, useRef, useState } from 'react';
import { useSessionStorage } from 'react-use';

import {
  FAVOURITE_ADDED_EVENT,
  FAVOURITE_REMOVED_EVENT,
  FAVOURITES_SUMMARY_FETCHED_EVENT,
} from 'client-site-chrome-ui/constants/favouritesEvents';
import type { Environment } from 'client-site-chrome-ui/environment';
import { fetchFavourites } from 'client-site-chrome-ui/services/favourites';

export const FAVORITES_STORAGE_KEY = 'CLIENT_SITE_CHROME__FAVOURITE_PRODUCTS';

export const useFavourites = (env: Environment = 'development') => {
  const [hasFavouriteProducts, setHasFavouritesProducts] = useState<boolean>(false);
  const [cachedHasFavouritesProducts, setCachedHasFavouritesProducts] = useSessionStorage<boolean>(
    FAVORITES_STORAGE_KEY,
    hasFavouriteProducts,
  );
  const cancelled = useRef(false);

  useEffect(() => {
    setHasFavouritesProducts(cachedHasFavouritesProducts);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateFavourites = useCallback(async () => {
    const favourites = await fetchFavourites(env);

    /* istanbul ignore next */
    if (favourites) {
      // random tests were failing because of dispatch event of null
      // so we added this optional chaining
      document?.dispatchEvent(
        new CustomEvent(FAVOURITES_SUMMARY_FETCHED_EVENT, {
          detail: favourites,
        }),
      );
      if (!cancelled.current) {
        const hasProducts = favourites.productCodes.length > 0;
        setHasFavouritesProducts(hasProducts);
        setCachedHasFavouritesProducts(hasProducts);
      }
    }
  }, [env, setCachedHasFavouritesProducts]);

  useEffect(() => {
    async function handleFavourites() {
      updateFavourites();
    }

    handleFavourites();
    return () => {
      cancelled.current = true;
    };
  }, [updateFavourites]);

  useEffect(() => {
    document.addEventListener(FAVOURITE_ADDED_EVENT, updateFavourites);
    document.addEventListener(FAVOURITE_REMOVED_EVENT, updateFavourites);

    return () => {
      document.removeEventListener(FAVOURITE_ADDED_EVENT, updateFavourites);
      document.removeEventListener(FAVOURITE_REMOVED_EVENT, updateFavourites);
    };
  }, [updateFavourites]);

  return hasFavouriteProducts;
};
